import React, { useCallback, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Button from '../../../../components/button';
import StringToHtml from '../../../../components/StringToHtml';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import {
  usePendingAcknowledgmentAnnouncementsQuery,
  useAcknowledgeHubAnnouncementMutation,
} from '../../__generated__/graphql';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';

const i18Prefix = 'people.acknowledgmentModal';

const AcknowledgmentAnnouncementModal: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const t = useNamespacedTranslation(i18Prefix);
  const { data, loading } = usePendingAcknowledgmentAnnouncementsQuery();
  const [acknowledge] = useAcknowledgeHubAnnouncementMutation();
  const errorHandler = useMutationErrorHandler();

  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(
    new Set(),
  );

  const allPending = data?.pendingAcknowledgmentAnnouncements ?? [];
  const acknowledgeable = allPending.filter((a) => a.notificationId);
  const totalCount = useRef(0);
  if (acknowledgeable.length > 0 && totalCount.current === 0) {
    totalCount.current = acknowledgeable.length;
  }

  const pendingAnnouncements = allPending.filter(
    (a) => a.notificationId && !acknowledgedIds.has(a.notificationId),
  );

  const currentAnnouncement = pendingAnnouncements[0] ?? null;
  const show = !loading && pendingAnnouncements.length > 0;

  const handleAcknowledge = useCallback(async () => {
    if (!currentAnnouncement?.notificationId || acknowledging) return;

    setAcknowledging(true);
    try {
      await acknowledge({
        variables: { notificationId: currentAnnouncement.notificationId },
      });
      setAcknowledgedIds((prev) =>
        new Set(prev).add(currentAnnouncement.notificationId!),
      );
    } catch (e) {
      errorHandler(e);
    } finally {
      setAcknowledging(false);
    }
  }, [currentAnnouncement, acknowledge, acknowledging, errorHandler]);

  if (!show || !currentAnnouncement) {
    return null;
  }

  return (
    <Modal
      show
      centered
      backdrop="static"
      keyboard={false}
      className="confirm-modal"
      contentClassName="border">
      <Modal.Header>
        <Modal.Title>{currentAnnouncement.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentAnnouncement.text && (
          <StringToHtml text={currentAnnouncement.text} />
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 d-flex justify-content-between align-items-center">
        {totalCount.current > 1 && (
          <small className="text-muted">
            {t('counter', {
              current: acknowledgedIds.size + 1,
              total: totalCount.current,
            })}
          </small>
        )}
        <Button
          onClick={handleAcknowledge}
          variant="primary"
          label={t('acknowledge')}
          isProcessing={acknowledging}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default AcknowledgmentAnnouncementModal;
